import React, { useMemo, useState } from "react"
import "../../styles/Components/table.css" // Import CSS file for styling
import { message, Tooltip } from "antd"
import { translations } from "../../utils/Translation/staticTranslation"
import { getUserCookies } from "../../utils/Cookies"
import { jwtDecode } from "jwt-decode"

const Table = ({
  columns,
  data,
  actions,
  onDelete,
  serverSideSorting = false,
  externalSortConfig = null,
  onSortChange,
  /** Fixed width for actions column (e.g. "200px") */
  actionsColumnWidth,
  /** "center" | "left" | "right" — actions header + cell alignment */
  actionsAlign = "left",
}) => {
  const lang = localStorage.getItem("language") || "ar"
  const status = translations[lang]?.status
  const product = translations[lang]?.productPage?.add_manual_page

  const table = translations[lang]?.common_table_comp

  // Check if user is super admin
  const highest_role =
    getUserCookies()?.token && jwtDecode(getUserCookies()?.token).highest_role
  const isSuperAdmin = highest_role === "SUPERADMIN"

  const [localSortConfig, setLocalSortConfig] = useState(null)
  const sortConfig = serverSideSorting ? externalSortConfig : localSortConfig

  const handleSort = (column) => {
    if (!column?.sortable) return

    const sortKey = column.sortKey || column.dataKey
    if (!sortKey) return

    const currentSort = serverSideSorting ? externalSortConfig : localSortConfig
    const nextSortConfig =
      currentSort && currentSort.key === sortKey
        ? {
            key: sortKey,
            direction: currentSort.direction === "asc" ? "desc" : "asc",
          }
        : { key: sortKey, direction: "asc" }

    if (serverSideSorting) {
      onSortChange?.(nextSortConfig)
      return
    }

    setLocalSortConfig(nextSortConfig)
  }

  const sortedData = useMemo(() => {
    if (!Array.isArray(data)) return []
    if (!sortConfig?.key) return data || []

    const activeColumn = columns.find(
      (col) => (col.sortKey || col.dataKey) === sortConfig.key
    )
    const shouldUseLocalSort =
      !serverSideSorting || activeColumn?.serverSortable === false

    if (!shouldUseLocalSort) return data || []

    const sortKey = sortConfig.key
    const directionFactor = sortConfig.direction === "desc" ? -1 : 1

    const getComparableValue = (row, key) => {
      const rawValue = row?.[key]

      // Industry can arrive as string, array of strings, or array of objects.
      // Normalize it to a single comparable text value.
      if (key === "industry") {
        if (typeof rawValue === "string") return rawValue
        if (Array.isArray(rawValue)) {
          return rawValue
            .map((item) => {
              if (typeof item === "string") return item
              if (item && typeof item === "object") {
                return lang === "ar" ? item.arabic || item.english : item.english || item.arabic
              }
              return ""
            })
            .filter(Boolean)
            .join(", ")
        }
      }

      return rawValue
    }

    const normalize = (value) => {
      if (value === null || value === undefined) return ""
      return value
    }

    return [...data].sort((a, b) => {
      const aRaw = normalize(getComparableValue(a, sortKey))
      const bRaw = normalize(getComparableValue(b, sortKey))

      const isPlaceholderValue = (value) => {
        const text = String(value ?? "").trim().toLowerCase()
        return text === "" || text === "—" || text === "-" || text === "n/a"
      }

      const aIsPlaceholder = isPlaceholderValue(aRaw)
      const bIsPlaceholder = isPlaceholderValue(bRaw)
      if (aIsPlaceholder !== bIsPlaceholder) {
        // Keep placeholder/empty values at the bottom in both asc/desc sorting.
        return aIsPlaceholder ? 1 : -1
      }

      if (sortKey === "joined_at") {
        const aDate = aRaw ? new Date(aRaw) : new Date(0)
        const bDate = bRaw ? new Date(bRaw) : new Date(0)
        if (aDate < bDate) return -1 * directionFactor
        if (aDate > bDate) return 1 * directionFactor
        return 0
      }

      const aNum =
        typeof aRaw === "number"
          ? aRaw
          : Number.isNaN(Number(aRaw))
          ? null
          : Number(aRaw)
      const bNum =
        typeof bRaw === "number"
          ? bRaw
          : Number.isNaN(Number(bRaw))
          ? null
          : Number(bRaw)

      if (aNum !== null && bNum !== null) {
        if (aNum < bNum) return -1 * directionFactor
        if (aNum > bNum) return 1 * directionFactor
        return 0
      }

      const aStr = String(aRaw)
      const bStr = String(bRaw)
      const result = aStr.localeCompare(bStr, undefined, {
        numeric: true,
        sensitivity: "base",
      })
      return result * directionFactor
    })
  }, [data, sortConfig, serverSideSorting, columns])

  // Check if we need to show actions column (existing actions or super admin delete)
  const hasActions =
    (actions && actions.length > 0) || (isSuperAdmin && onDelete)

  // Helper function to convert country code to country name
  const getCountryName = (code) => {
    if (!code) return lang === "ar" ? "غير معروف" : "Unknown"
    try {
      const formatter = new Intl.DisplayNames([lang === "ar" ? "ar" : "en"], {
        type: "region",
      })
      return (
        formatter.of(String(code).toUpperCase()) || String(code).toUpperCase()
      )
    } catch {
      return String(code).toUpperCase()
    }
  }

  // Helper function to get flag URL by alpha-2 code (ISO)
  const getFlagUrl = (code) => {
    if (!code) return null
    const lower = String(code).toLowerCase()
    // Using Flagcdn CDN (free, fast, reliable)
    return `https://flagcdn.com/w40/${lower}.png`
  }

  return (
    <div className="table-container">
      <table className="custom-table">
        <thead className="table-header">
          <tr>
            {columns.map((col, index) => {
              const isSortable = !!col.sortable
              const columnSortKey = col.sortKey || col.dataKey
              const isActiveSortColumn =
                !!sortConfig?.key && sortConfig.key === columnSortKey
              const sortIcon = isActiveSortColumn
                ? sortConfig.direction === "asc"
                  ? "▲"
                  : "▼"
                : "▼"

              return (
                <th
                  key={index}
                  className={`text-14 text-capitalize ${
                    isSortable ? "sortable-header" : ""
                  }`}
                  style={{
                    width:
                      col.width ||
                      (col.label === "PRODUCT NAME" ? "25%" : undefined),
                    position: "relative",
                  }}
                  onClick={() => handleSort(col)}
                >
                  {col.label}
                  {isSortable && (
                    <span className="sort-indicator">
                      {sortIcon}
                    </span>
                  )}
                </th>
              )
            })}
            {hasActions && (
              <th
                className={`text-14 text-capitalize org-table-actions-header${
                  actionsAlign === "center"
                    ? " org-table-actions-header--center"
                    : actionsAlign === "right"
                      ? " org-table-actions-header--right"
                      : ""
                }`}
                style={
                  actionsColumnWidth ? { width: actionsColumnWidth } : undefined
                }
              >
                {table?.actions || (lang === "ar" ? "الإجراءات" : "Actions")}
              </th>
            )}
          </tr>
        </thead>

        {sortedData.length > 0 ? (
          <tbody className="table-body">
            {sortedData.map((row, rowIndex) => (
              <tr key={rowIndex}>
                {columns.map((col, colIndex) => (
                  <td
                    key={colIndex}
                    className={`text-14 text-capitalize${col.cellClass ? ` ${col.cellClass}` : ""}`}
                  >
                    {col.dataKey === "file_status" ? (
                      <span
                        className={`status ${
                          row[col.dataKey] === 2
                            ? "pending"
                            : row[col.dataKey] === 3
                            ? "rejected"
                            : row[col.dataKey] === 4
                            ? "active"
                            : "pending"
                        }`}
                      >
                        {{
                          1: status.pending,
                          4: status?.completed,
                          2: status?.processing,
                          3: status?.failed,
                        }[row[col.dataKey]] || ""}
                      </span>
                    ) : col.dataKey === "uploaded_at" ? (
                      <>{new Date(row[col.dataKey]).toLocaleString()}</>
                    ) : col.dataKey === "industry" ? (
                      (() => {
                        // Handle if industry is a string (comma-separated values)
                        if (typeof row.industry === "string") {
                          const industries = row.industry
                            .split(",")
                            .map((i) => i.trim())

                          if (industries.length > 1) {
                            return (
                              <Tooltip title={row.industry} placement="top">
                                <span style={{ cursor: "pointer" }}>
                                  {industries[0]}...
                                </span>
                              </Tooltip>
                            )
                          }
                          return row.industry
                        }

                        // Handle if industry is an array of objects
                        if (!row.industry || !Array.isArray(row.industry)) {
                          return ""
                        }

                        const industryNames = row.industry.map((industry) =>
                          typeof industry === "string"
                            ? industry
                            : lang === "ar"
                            ? industry.arabic
                            : industry.english
                        )
                        const allIndustries = industryNames.join(", ")

                        if (industryNames.length > 1) {
                          const firstIndustry = industryNames[0]
                          return (
                            <Tooltip title={allIndustries} placement="top">
                              <span style={{ cursor: "pointer" }}>
                                {firstIndustry}...
                              </span>
                            </Tooltip>
                          )
                        }
                        return allIndustries
                      })()
                    ) : col.dataKey === "country" ? (
                      <div
                          className= "country-cell"
                      >
                        {row[col.dataKey] && getFlagUrl(row[col.dataKey]) && (
                          <img
                            src={getFlagUrl(row[col.dataKey])}
                            alt={`${row[col.dataKey]} flag`}
                            style={{
                              width: "20px",
                              height: "20px",
                              objectFit: "cover",
                              borderRadius: "50%",
                            }}
                            onError={(e) => {
                              e.target.style.display = "none"
                            }}
                          />
                        )}
                        <span>{getCountryName(row[col.dataKey])}</span>
                      </div>
                    ) : col.dataKey === "joined_at" ? (
                      <span style={{ textTransform: "none" }}>
                        {row[col.dataKey]
                          ? new Date(
                              `${String(row[col.dataKey]).slice(0, 10)}T12:00:00`
                            ).toLocaleDateString(
                              lang === "ar" ? "ar-SA" : "en-GB",
                              {
                                year: "numeric",
                                month: "short",
                                day: "numeric",
                              }
                            )
                          : "—"}
                      </span>
                    ) : col.dataKey === "product_count" ? (
                      <span style={{ textTransform: "none" }}>
                        {Number(row[col.dataKey] ?? 0).toLocaleString("en-US")}
                      </span>
                    ) : col.dataKey === "org_member_status" ? (
                      <span
                        className={`status ${row._orgStatusClass || ""}`.trim()}
                      >
                        {row[col.dataKey] || "—"}
                      </span>
                    ) : col.dataKey === "features_display" &&
                      col.cellClass === "org-cell-features" &&
                      Array.isArray(row.feature_tags) &&
                      row.feature_tags.length > 0 ? (
                      <div className="org-feature-tags">
                        {row.feature_tags.map((tag) => (
                          <span key={tag} className="org-feature-tag">
                            {tag}
                          </span>
                        ))}
                      </div>
                    ) : col.dataKey === "active_status" ||
                      col.dataKey === "status" ||
                      col.dataKey === "is_active" ? (
                      (() => {
                        const raw = row[col.dataKey]
                        const lower =
                          typeof raw === "string"
                            ? raw.trim().toLowerCase()
                            : raw

                        const isActive =
                          raw === true ||
                          lower === "active" ||
                          lower === "true"

                        const isInactive =
                          raw === false ||
                          lower === "inactive" ||
                          lower === "false"

                        const isPending = lower === "pending"

                        const label = isActive
                          ? status?.active
                          : isInactive
                          ? status?.inactive
                          : isPending
                          ? status?.pending
                          : typeof raw === "string" && raw.trim()
                          ? raw.trim()
                          : ""

                        const statusClass = isActive
                          ? "active"
                          : isInactive || isPending
                          ? "pending"
                          : ""

                        return (
                          <span className={`status ${statusClass}`.trim()}>
                            {label || "—"}
                          </span>
                        )
                      })()
                    ) : col.dataKey === "request_status" ? (
                      <span
                        className={`status ${
                          row[col.dataKey] === 1
                            ? "pending"
                            : row[col.dataKey] === 3
                            ? "rejected"
                            : row[col.dataKey] === 5
                            ? "rejected"
                            : row[col.dataKey] === 6
                            ? "blocked" // ✅ New blocked status
                            : "waiting"
                        }`}
                      >
                        {{
                          5: status?.permanently_rejected,
                          0: status?.waiting,
                          1: status?.pending,
                          3: status?.rejected,
                          6: status?.blocked || "Blocked", // ✅ Use translations or fallback
                        }[row[col.dataKey]] || ""}
                      </span>
                    ) : col.dataKey === "image_url" ||
                      col.dataKey === "product_url" ? (
                      <div
                        onClick={() => {
                          if (col.dataKey === "product_url") {
                            window.open(row[col.dataKey], "_blank")
                          } else if (col.dataKey === "image_url") {
                            navigator.clipboard.writeText(row[col.dataKey])
                            message.success(table?.image_url_copied_clipboard)
                          }
                        }}
                        className={`pxy-6-12 border-radius-4 pointer ${
                          col.dataKey === "image_url"
                            ? "table-image-url"
                            : "table-product-url"
                        }`}
                      >
                        <img
                          src="/table/imageurl.svg"
                          alt=""
                          className="mr-10"
                        />
                        <span
                          className={` ${
                            col.dataKey === "image_url"
                              ? "text-026AA2"
                              : "text-0032AA"
                          }  text-14 font-400`}
                        >
                          {" "}
                          {col.dataKey === "image_url"
                            ? product?.image_url
                            : product?.product_url}
                        </span>
                      </div>
                    ) : col.dataKey === "price_amount" ? (
                      <>
                        {" "}
                        {row[col.dataKey]} {row["price_unit"]}
                      </>
                    ) : col.dataKey === "category" ? (
                      <>
                        {row[col.dataKey]
                          .split("-") // Split by hyphen
                          .map(
                            (word) =>
                              word.charAt(0).toUpperCase() + word.slice(1)
                          ) // Capitalize each word
                          .join(" ")}
                      </>
                    ) : col.dataKey === "email_address" ||
                      col.dataKey === "email" ? (
                      <p className="text-lower mb-0-imp">
                        {String(row[col.dataKey] || "").toLowerCase()}
                      </p>
                    ) : col.salesRequestBadge && row.from_sales_request ? (
                      // Merchants onboarded through the sales pipeline: the
                      // account is an ordinary OWNER, the badge is only so the
                      // admin can tell where the merchant came from.
                      <span className="vendor-with-source">
                        <span>{row[col.dataKey]}</span>
                        <span className="source-badge source-badge-sales">
                          {table?.from_sales_request || "Sales request"}
                        </span>
                      </span>
                    ) : (
                      <> {row[col.dataKey]}</>
                    )}
                  </td>
                ))}
                {hasActions && (
                  <td
                    className={`org-actions-cell${
                      actionsAlign === "center"
                        ? " org-actions-cell--center"
                        : actionsAlign === "right"
                          ? " org-actions-cell--right"
                          : ""
                    }`}
                    style={
                      actionsColumnWidth ? { width: actionsColumnWidth } : undefined
                    }
                  >
                    <div
                      className={`actions-container org-row-actions${
                        actionsAlign === "center"
                          ? " org-row-actions--center"
                          : actionsAlign === "right"
                            ? " org-row-actions--right"
                            : ""
                      }`}
                    >
                      {actions &&
                        actions.map((action, actionIndex) => {
                          if (action.condition && !action.condition(row)) {
                            if (action.type === "text") {
                              return (
                                <div
                                  key={`placeholder-${actionIndex}`}
                                  className="action-placeholder"
                                ></div>
                              )
                            }
                            return null
                          }

                          return (
                            <React.Fragment
                              key={`${rowIndex}-action-${actionIndex}`}
                            >
                              {action.type === "image" ? (
                                <img
                                  className="mr-20 pointer"
                                  src={action.icon}
                                  alt=""
                                  onClick={() => action.onClick(row)}
                                />
                              ) : action.type === "switch" ? (
                                <span className="">{action.switch(row)}</span>
                              ) : action.type === "text" ? (
                                (() => {
                                  const actionDisabled =
                                    typeof action.disabled === "function"
                                      ? action.disabled(row)
                                      : Boolean(action.disabled)
                                  const updateFrozen =
                                    action.text === "Update" &&
                                    row.request_status === 5
                                  const isDisabled =
                                    actionDisabled || updateFrozen
                                  return (
                                    <span
                                      className={`${action.className} ${
                                        isDisabled ? "disabled" : ""
                                      }`}
                                      title={
                                        isDisabled
                                          ? action.disabledTitle
                                          : undefined
                                      }
                                      onClick={() => {
                                        if (!isDisabled) action.onClick(row)
                                      }}
                                      role="button"
                                      tabIndex={isDisabled ? -1 : 0}
                                    >
                                      {action.text}
                                    </span>
                                  )
                                })()
                              ) : action.type === "button" ? (
                                <span onClick={() => action.onClick(row)}>
                                  {action.button(row)}
                                </span>
                              ) : (
                                action
                              )}
                            </React.Fragment>
                          )
                        })}
                      {/* Delete icon for super admin */}
                      {isSuperAdmin && onDelete && (
                        <img
                          className="mr-20 pointer"
                          src="/store/trash.svg"
                          alt="Delete"
                          onClick={() => onDelete(row)}
                        />
                      )}
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        ) : (
          <tbody>
            <tr>
              <td
                className="text-center"
                colSpan={columns.length + (hasActions ? 1 : 0)}
              >
                No data available
              </td>
            </tr>
          </tbody>
        )}
      </table>
    </div>
  )
}

export default Table
